import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LayoutService } from '../layout/layout.service';

/**
 * Service to manage body scroll behavior, particularly useful for preventing
 * scroll on mobile devices when overlays or splash screens are active.
 */
@Injectable({
  providedIn: 'root',
})
export class BodyScrollManagerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly layoutService = inject(LayoutService);

  private scrollDisableCount = 0;
  private originalOverflow = '';
  private originalPosition = '';
  private originalTop = '';
  private scrollPosition = 0;

  /**
   * Disables body scroll on mobile devices
   * Uses a counter system to handle multiple components requesting scroll disable
   */
  disableScroll(): void {
    if (!isPlatformBrowser(this.platformId) || !this.layoutService.isMobile) {
      return;
    }

    this.scrollDisableCount++;

    if (this.scrollDisableCount === 1) {
      this.storeOriginalStyles();
      this.applyScrollDisableStyles();
    }
  }

  /**
   * Enables body scroll on mobile devices
   * Uses a counter system to handle multiple components requesting scroll disable
   */
  enableScroll(): void {
    if (!isPlatformBrowser(this.platformId) || !this.layoutService.isMobile) {
      return;
    }

    if (this.scrollDisableCount > 0) {
      this.scrollDisableCount--;
    }

    if (this.scrollDisableCount === 0) {
      this.restoreOriginalStyles();
    }
  }

  /**
   * Force enables scroll regardless of counter state
   * Useful for emergency cleanup
   */
  forceEnableScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.scrollDisableCount = 0;
    
    if (this.originalOverflow !== '' || this.originalPosition !== '') {
      this.restoreOriginalStyles();
    }
  }

  /**
   * Returns whether scroll is currently disabled
   */
  get isScrollDisabled(): boolean {
    return this.scrollDisableCount > 0;
  }

  /**
   * Store original body styles and scroll position before applying scroll disable
   */
  private storeOriginalStyles(): void {
    const body = document.body;
    
    this.originalOverflow = body.style.overflow || '';
    this.originalPosition = body.style.position || '';
    this.originalTop = body.style.top || '';
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  /**
   * Apply styles to disable scroll and maintain visual position
   */
  private applyScrollDisableStyles(): void {
    const body = document.body;
    
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollPosition}px`;
    body.style.width = '100%';
    body.style.height = '100%';
  }

  /**
   * Restore original body styles and scroll position
   */
  private restoreOriginalStyles(): void {
    const body = document.body;
    
    body.style.overflow = this.originalOverflow;
    body.style.position = this.originalPosition;
    body.style.top = this.originalTop;
    body.style.removeProperty('width');
    body.style.removeProperty('height');
    
    window.scrollTo(0, this.scrollPosition);
  }
}
